import { useEffect, useState, type FormEvent } from 'react';
import { Check, ChevronDown, ChevronUp, Clock3, Plus, RotateCcw, Search, Send, ShieldAlert, UserCheck, UserMinus, X } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { Commission } from '../types';
import { commissionStatus, currencyInputToCents, formatMoney, formatTime } from '../utils';
import { ActionSheet, Badge, Button, CollapsibleCard, EmptyBlock, Fab, Field, LoadingBlock, PageHeader, Panel, Select, Textarea, TextInput } from '../components/ui';

const filters = [
  ['all', '全部'], ['open', '待接单'], ['taken', '进行中'], ['submitted', '待确认'], ['done', '已完成'], ['cancelled', '已取消'], ['mine', '与我相关']
] as const;

const commissionGroups = [
  { key: 'open', label: '未接取', description: '等待玩家接单', statuses: ['open'] },
  { key: 'active', label: '未完成 · 进行中', description: '已接单或等待发布者确认', statuses: ['taken', 'submitted'] },
  { key: 'done', label: '已完成', description: '报酬已经结算', statuses: ['done'] },
  { key: 'cancelled', label: '已取消', description: '委托已经撤销或终止', statuses: ['cancelled'] }
] as const;

function fuzzyCommissionMatch(item: Commission, query: string) {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  const fields = [item.title, item.description, item.type, item.creatorName, item.takerName || '', commissionStatus[item.status].label]
    .map(value => String(value || '').toLowerCase());
  const match = (field: string, token: string) => {
    if (field.includes(token)) return true;
    let cursor = 0;
    for (const char of field) {
      if (char === token[cursor]) cursor += 1;
      if (cursor === token.length) return true;
    }
    return false;
  };
  return tokens.every(token => fields.some(field => match(field, token)));
}

export function CommissionsPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<Commission[] | null>(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({ done: true, cancelled: true });
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState<Commission | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const request = filter === 'mine' ? '?mine=1' : filter === 'all' ? '' : '?status=' + filter;
    const data = await api<{ commissions: Commission[] }>('/api/commissions' + request);
    setItems(data.commissions);
  };

  useEffect(() => {
    setItems(null);
    void load().catch(() => toast('委托列表加载失败', 'error'));
  }, [filter, user?.id]);

  const needBinding = () => {
    if (!user) { onNeedAuth(); return true; }
    if (!user.boundToGame) { toast('请先绑定游戏角色后再使用委托功能', 'error'); return true; }
    return false;
  };

  const openForm = () => {
    if (needBinding()) return;
    setFormOpen(true);
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needBinding()) return;
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const deadline = String(form.get('deadline') || '');
    const payload = {
      title: String(form.get('title') || ''),
      type: String(form.get('type') || '其他'),
      description: String(form.get('description') || ''),
      rewardCents: currencyInputToCents(String(form.get('reward') || '0')),
      deadlineAt: deadline ? new Date(deadline).getTime() : null
    };
    setBusy(true);
    try {
      await api('/api/commissions', { method: 'POST', body: JSON.stringify(payload) });
      formEl.reset();
      setFormOpen(false);
      await Promise.all([load(), refresh()]);
      toast('委托已发布，报酬已冻结', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发布失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const action = async (item: Commission, endpoint: string, title: string, body: string, danger = false, requireBinding = true) => {
    if (requireBinding && needBinding()) return;
    const approved = await confirm({ title, body, danger, confirmText: danger ? '确认执行' : '确认' });
    if (!approved) return;
    try {
      await api('/api/commissions/' + item.id + endpoint, { method: 'POST' });
      await Promise.all([load(), refresh()]);
      setDetail(null);
      toast('委托状态已更新', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '操作失败', 'error');
    }
  };

  const appealCommission = async (item: Commission) => { if (needBinding()) return; const reason = window.prompt('请输入上诉原因', '委托争议'); if (!reason) return; try { await api('/api/v2/commissions/' + item.id + '/appeal', { method:'POST', body: JSON.stringify({ reason }) }); toast('委托上诉已提交', 'success'); } catch (e) { toast(e instanceof ApiError ? e.message : '提交失败', 'error'); } };

  const visibleItems = (items || []).filter(item => fuzzyCommissionMatch(item, query));
  const groups = commissionGroups.map(group => ({ ...group, items: visibleItems.filter(item => (group.statuses as readonly string[]).includes(item.status)) })).filter(group => group.items.length > 0);
  const toggleGroup = (key: string) => setCollapsedGroups(current => ({ ...current, [key]: !current[key] }));
  const selectFilter = (nextFilter: string) => {
    setFilter(nextFilter);
    const groupKey = nextFilter === 'taken' || nextFilter === 'submitted' ? 'active' : nextFilter;
    if (['open', 'active', 'done', 'cancelled'].includes(groupKey)) {
      setCollapsedGroups(current => ({ ...current, [groupKey]: false }));
    }
  };

  const detailActions = (item: Commission) => {
    const isCreator = user?.id === item.creatorId;
    const isTaker = user?.id === item.takerId;
    return (
      <>
        {item.status === 'open' && !user && <Button variant="primary" onClick={onNeedAuth}>登录后接单</Button>}
        {item.status === 'open' && user && isCreator && <Button variant="danger" onClick={() => void action(item, '/cancel', '确认要撤销这笔委托？', '报酬将退回你的余额。', true)}><RotateCcw size={16} /> 撤销委托</Button>}
        {item.status === 'open' && user && !isCreator && <Button variant="primary" onClick={() => void action(item, '/take', '承接委托', '接单后双方不能自行取消。')}><UserCheck size={16} /> 承接委托</Button>}
        {item.status === 'taken' && isTaker && <Button variant="primary" onClick={() => void action(item, '/submit', '提交完成', '提交后等待发布者确认付款。')}><Send size={16} /> 提交完成</Button>}
        {item.status === 'submitted' && isCreator && <Button variant="primary" onClick={() => void action(item, '/confirm', '确认付款', '确认后托管报酬将立即支付给接单者。')}><Check size={16} /> 确认付款</Button>}
        {permissions.isStaff && item.status === 'taken' && <Button variant="danger" onClick={() => void action(item, '/release-taker', '取消接单并重新开放？', `将取消 ${item.takerName || '接单者'} 的接单，委托会重新回到待接单，托管报酬保持冻结。`, true, false)}><UserMinus size={16} /> 取消接单</Button>}
        {permissions.isStaff && !['done', 'cancelled'].includes(item.status) && <Button variant="danger" onClick={() => void action(item, '/cancel', '管理员强制撤销', '托管报酬将退回发布者，操作会写入审计日志。', true, false)}><ShieldAlert size={16} /> 强制撤销</Button>}
      </>
    );
  };

  const renderCommission = (item: Commission) => {
    const state = commissionStatus[item.status];
    const isCreator = user?.id === item.creatorId;
    const isTaker = user?.id === item.takerId;
    return (
      <CollapsibleCard
        key={item.id}
        className={'commission-card commission-card-' + item.status}
        eyebrow={item.type}
        title={item.title}
        subtitle={`${item.creatorName} 发布 · ${formatTime(item.createdAt)}`}
        badge={<Badge tone={state.tone}>{state.label}</Badge>}
        trailing={<strong className="price-tag">{formatMoney(item.rewardCents, settings.currency)}</strong>}
      >
        {item.description && <p className="record-description">{item.description}</p>}
        <div className="record-meta">
          {item.takerName && <span>接单：{item.takerName}</span>}
          {item.deadlineAt && <span><Clock3 size={14} /> 截止 {formatTime(item.deadlineAt)}</span>}
          {item.status === 'done' && item.doneAt && <span>完成：{formatTime(item.doneAt)}</span>}
          {item.status === 'cancelled' && item.cancelledAt && <span>取消：{formatTime(item.cancelledAt)}</span>}
        </div>
        <footer className="record-actions">
          {item.status === 'open' && !isCreator && <Button variant="primary" onClick={() => void action(item, '/take', '承接委托', '接单后双方不能自行取消。')}><UserCheck size={16} /> 承接委托</Button>}
          {item.status === 'taken' && isTaker && <Button variant="primary" onClick={() => void action(item, '/submit', '提交完成', '提交后等待发布者确认付款。')}><Send size={16} /> 提交完成</Button>}
          {item.status === 'submitted' && isCreator && <Button variant="primary" onClick={() => void action(item, '/confirm', '确认完成', '确认后托管的报酬会立即支付给接单者。')}><Check size={16} /> 确认完成</Button>}
          <Button variant="ghost" onClick={() => setDetail(item)}>详情</Button>{user && (user.id === item.creatorId || user.id === item.takerId) && ['taken','submitted'].includes(item.status) && <Button variant="danger" onClick={() => void appealCommission(item)}>上诉</Button>}
          {item.status === 'open' && isCreator && <Button variant="danger" onClick={() => void action(item, '/cancel', '确认要撤销这笔委托？', '报酬将退回你的余额。', true)}><RotateCcw size={16} /> 撤销</Button>}
          {permissions.isStaff && item.status === 'taken' && <Button variant="danger" onClick={() => void action(item, '/release-taker', '取消接单并重新开放？', `将取消 ${item.takerName || '接单者'} 的接单，委托会重新回到待接单，托管报酬保持冻结。`, true, false)}><UserMinus size={16} /> 取消接单</Button>}
          {permissions.isStaff && !['done', 'cancelled'].includes(item.status) && <Button variant="danger" onClick={() => void action(item, '/cancel', '管理员强制撤销', '托管报酬将退回发布者，操作会写入审计日志。', true, false)}><ShieldAlert size={16} /> 强制撤销</Button>}
        </footer>
      </CollapsibleCard>
    );
  };

  return (
    <>
      <PageHeader title="委托大厅" description="报酬在发布时冻结；管理员可以取消不作为的接单并重新开放，也可以强制撤销整笔委托。" action={<a className="button ghost" href="#/tutorials">教学视频</a>} />

      <ActionSheet open={formOpen} onClose={() => setFormOpen(false)} title="发布委托" eyebrow="NEW DISPATCH" description="发布时立即冻结报酬，接单后双方不能自行取消。">
        <form className="stack-form" onSubmit={create}>
          <Field label="委托标题"><TextInput name="title" maxLength={40} required placeholder="例：收购 64 个钻石" /></Field>
          <Field label="委托类型"><Select name="type" defaultValue="物品求购"><option>物品求购</option><option>悬赏任务</option><option>建造代工</option><option>其他</option></Select></Field>
          <Field label="详细说明"><Textarea name="description" rows={4} maxLength={1000} placeholder="交付要求、验收标准、时间地点等" /></Field>
          <div className="form-grid">
            <Field label={'报酬（' + settings.currency + '）'}><TextInput name="reward" type="number" min="0.01" step="0.01" required /></Field>
            <Field label="截止时间（可选）"><TextInput name="deadline" type="datetime-local" /></Field>
          </div>
          <Button variant="primary" type="submit" loading={busy} className="wide"><Plus size={17} /> 发布并冻结报酬</Button>
        </form>
      </ActionSheet>

      <Panel title={'委托列表' + (items ? ' · ' + visibleItems.length : '')} eyebrow="DISPATCH BOARD" action={<div className="filter-chips mobile-scroll-tabs">{filters.map(([key, label]) => <button key={key} className={filter === key ? 'active' : ''} onClick={() => selectFilter(key)}>{label}</button>)}</div>}>
        <div className="commission-search-row">
          <label className="commission-search-box"><Search size={16} /><input className="input" value={query} onChange={event => setQuery(event.target.value)} placeholder="模糊搜索标题、说明、类型、发布者或接单者" />{query && <button type="button" aria-label="清空委托搜索" onClick={() => setQuery('')}><X size={15} /></button>}</label>
          <span>{items ? '当前显示 ' + visibleItems.length + ' 条委托' : '正在读取委托'}</span>
        </div>
        {!items ? <LoadingBlock /> : visibleItems.length === 0 ? <EmptyBlock title={query ? '没有找到匹配的委托' : '没有符合条件的委托'} description={query ? '换一个关键词，或清空搜索查看全部委托。' : '发布第一笔委托，让成员开始接单。'} action={query ? <Button variant="ghost" onClick={() => setQuery('')}>清空搜索</Button> : <Button variant="primary" onClick={openForm}><Plus size={16} /> 发布委托</Button>} /> : (
          <div className="commission-groups">
            {groups.map(group => {
              const groupOpen = Boolean(query.trim()) || !collapsedGroups[group.key];
              return <section className={'commission-group commission-group-' + group.key + (groupOpen ? '' : ' is-collapsed')} key={group.key}>
                <button type="button" className="commission-group-head" aria-expanded={groupOpen} onClick={() => toggleGroup(group.key)}>
                  <span className="commission-group-dot" />
                  <div><strong>{group.label}</strong><small>{group.description}</small></div>
                  <b>{group.items.length}</b>
                  {groupOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {groupOpen && <div className="card-list commission-group-body">{group.items.map(renderCommission)}</div>}
              </section>;
            })}
          </div>
        )}
      </Panel>

      <Fab label="发布委托" onClick={openForm} />

      {detail && (
        <div className="modal-backdrop" onMouseDown={event => event.target === event.currentTarget && setDetail(null)}>
          <div className="modal-card detail-modal">
            <span className="eyebrow">DISPATCH DETAIL</span>
            <h2>{detail.title}</h2>
            <div className="record-meta"><Badge tone={commissionStatus[detail.status].tone}>{commissionStatus[detail.status].label}</Badge><span>{detail.type}</span><span>{formatMoney(detail.rewardCents, settings.currency)}</span></div>
            <p className="record-description">{detail.description || '发布者没有填写详细说明。'}</p>
            <dl className="detail-list"><div><dt>发布者</dt><dd>{detail.creatorName}</dd></div><div><dt>接单者</dt><dd>{detail.takerName || '暂无'}</dd></div><div><dt>发布时间</dt><dd>{formatTime(detail.createdAt)}</dd></div>{detail.deadlineAt && <div><dt>截止时间</dt><dd>{formatTime(detail.deadlineAt)}</dd></div>}</dl>
            <div className="modal-actions"><Button variant="ghost" onClick={() => setDetail(null)}>关闭</Button>{detailActions(detail)}</div>
          </div>
        </div>
      )}
    </>
  );
}
